import { Component, computed, effect, inject, input, linkedSignal, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlContainer, FormControl, FormGroupDirective, ReactiveFormsModule, TouchedChangeEvent } from '@angular/forms';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatInput } from '@angular/material/input';
import { DkCurrencyPipe } from '../../../../../shared/pipes/dk-currency';
import { MatTooltip } from '@angular/material/tooltip';
import { filter, merge } from 'rxjs';

@Component({
  selector: 'adm-price-ex-incl-vat',
  templateUrl: './price-ex-incl-vat.component.html',
  styleUrls: ['./price-ex-incl-vat.component.scss'],
  imports: [ReactiveFormsModule, MatFormField, MatLabel, MatInput, MatTooltip],
  viewProviders: [
    {
      provide: ControlContainer,
      useFactory: () => inject(ControlContainer, { skipSelf: true }),
    },
  ],
})
export class AdmPriceExInclVatComponent {
  headline = input.required<string>();
  formControlNameExclVat = input.required<string>();
  formControlNameInclVat = input.required<string>();
  tooltipText = input<string>('');
  initialExclVat = input<number | null>(null);
  initialInclVat = input<number | null>(null);
  resetTrigger = input<number>(0);

  private readonly currencyPipe = new DkCurrencyPipe();
  private parentContainer = inject(ControlContainer);
  VAT = 0.25;

  exclVat = linkedSignal(() => this.initialExclVat());
  inclVat = linkedSignal(() => this.initialInclVat());
  exclVatFocused = signal<boolean>(false);
  inclVatFocused = signal<boolean>(false);
  private rawExclText = signal<string>('');
  private rawInclText = signal<string>('');

  public exclVatDisplayControl = new FormControl();
  public exclVatErrorStateMatcher: ErrorStateMatcher = {
    isErrorState: (): boolean => {
      const parentFormGroup = (this.parentContainer as FormGroupDirective)?.form;
      if (!parentFormGroup) return false;
      const control = parentFormGroup.get(this.formControlNameExclVat());
      return !!control?.invalid && !!control?.touched;
    },
  };

  public inclVatErrorStateMatcher: ErrorStateMatcher = {
    isErrorState: (): boolean => {
      const parentFormGroup = (this.parentContainer as FormGroupDirective)?.form;
      if (!parentFormGroup) return false;
      const control = parentFormGroup.get(this.formControlNameExclVat());
      return !!control?.invalid && !!control?.touched;
    },
  };

  public inclVatDummyControl = new FormControl();
  private hasHandledInitialResetTrigger = false;

  constructor() {
    // Keep the excl vat display control's value in sync
    effect(() => {
      const display = this.exclVatDisplay();
      if (this.exclVatDisplayControl.value !== display) {
        this.exclVatDisplayControl.setValue(display, { emitEvent: false });
      }
    });

    // Keep the incl vat dummy control's displayed value in sync
    effect(() => {
      const display = this.inclVatDisplay();
      if (this.inclVatDummyControl.value !== display) {
        this.inclVatDummyControl.setValue(display, { emitEvent: false });
      }
    });

    // React to user input via valueChanges
    this.exclVatDisplayControl.valueChanges.pipe(takeUntilDestroyed()).subscribe(value => this.updateExcl(value ?? ''));

    this.inclVatDummyControl.valueChanges.pipe(takeUntilDestroyed()).subscribe(value => this.updateIncl(value ?? ''));

    // Propagate touched state from display controls to parent form control
    merge(this.exclVatDisplayControl.events, this.inclVatDummyControl.events)
      .pipe(
        filter((event): event is TouchedChangeEvent => event instanceof TouchedChangeEvent && event.touched),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        const parentFormGroup = (this.parentContainer as FormGroupDirective)?.form;
        if (parentFormGroup) {
          const control = parentFormGroup.get(this.formControlNameExclVat());
          control?.markAsTouched();
        }
      });

    // Reset local display state whenever parent type changes.
    effect(() => {
      this.resetTrigger();
      if (!this.hasHandledInitialResetTrigger) {
        this.hasHandledInitialResetTrigger = true;
        return;
      }

      this.exclVat.set(null);
      this.inclVat.set(null);
      this.rawExclText.set('');
      this.rawInclText.set('');
      this.exclVatDisplayControl.setValue('', { emitEvent: false });
      this.inclVatDummyControl.setValue('', { emitEvent: false });
    });
  }

  inclVatComputed = computed(() => {
    const excl = this.exclVat();
    return excl !== null ? +(excl * (1 + this.VAT)).toFixed(2) : null;
  });

  exclVatComputed = computed(() => {
    const incl = this.inclVat();
    return incl !== null ? +(incl / (1 + this.VAT)).toFixed(2) : null;
  });

  exclVatDisplay = computed(() => {
    if (this.exclVatFocused()) {
      return this.rawExclText();
    }
    const value = this.exclVat() ?? this.exclVatComputed();
    return this.currencyPipe.transform(value) ?? '';
  });

  inclVatDisplay = computed(() => {
    if (this.inclVatFocused()) {
      return this.rawInclText();
    }
    const value = this.inclVat() ?? this.inclVatComputed();
    return this.currencyPipe.transform(value) ?? '';
  });

  updateExcl(value: string) {
    this.rawExclText.set(value);
    const normalized = value.replace(',', '.');
    const num = parseFloat(normalized);
    if (!isNaN(num)) {
      const rounded = +num.toFixed(2);
      this.exclVat.set(rounded);
      this.inclVat.set(null);
      // Update parent form control
      const parentFormGroup = (this.parentContainer as FormGroupDirective)?.form;
      if (parentFormGroup) {
        const controlName = this.formControlNameExclVat();
        const control = parentFormGroup.get(controlName);
        if (control) {
          control.setValue(rounded);
          control.markAsDirty();
        }
      }
    } else {
      this.exclVat.set(null);
      // Clear parent form control if invalid
      const parentFormGroup = (this.parentContainer as FormGroupDirective)?.form;
      if (parentFormGroup) {
        const controlName = this.formControlNameExclVat();
        const control = parentFormGroup.get(controlName);
        if (control) {
          control.setValue(null);
          control.markAsDirty();
        }
      }
    }
    this.inclVatDummyControl.updateValueAndValidity({ emitEvent: false });
  }

  updateIncl(value: string) {
    this.rawInclText.set(value);
    const normalized = value.replace(',', '.');
    const num = parseFloat(normalized);
    if (!isNaN(num)) {
      const rounded = +num.toFixed(2);
      this.inclVat.set(rounded);
      this.exclVat.set(null);
      // Update parent form control with computed excl VAT value
      const parentFormGroup = (this.parentContainer as FormGroupDirective)?.form;
      if (parentFormGroup) {
        const controlName = this.formControlNameExclVat();
        const control = parentFormGroup.get(controlName);
        if (control) {
          const exclValue = +(rounded / (1 + this.VAT)).toFixed(2);
          control.setValue(exclValue);
          control.markAsDirty();
        }
      }
    } else {
      this.inclVat.set(null);
      // Clear parent form control if invalid
      const parentFormGroup = (this.parentContainer as FormGroupDirective)?.form;
      if (parentFormGroup) {
        const controlName = this.formControlNameExclVat();
        const control = parentFormGroup.get(controlName);
        if (control) {
          control.setValue(null);
          control.markAsDirty();
        }
      }
    }
    this.inclVatDummyControl.updateValueAndValidity({ emitEvent: false });
  }

  onExclFocus() {
    this.exclVatFocused.set(true);
    const value = this.exclVat();
    this.rawExclText.set(value !== null ? value.toString() : '');
  }

  onExclBlur() {
    this.exclVatFocused.set(false);
  }

  onInclFocus() {
    this.inclVatFocused.set(true);
    const value = this.inclVat() ?? this.inclVatComputed();
    this.rawInclText.set(value !== null ? value.toString() : '');
  }

  onInclBlur() {
    this.inclVatFocused.set(false);
  }
}
